/**
 * Route registry — single source of truth for BNM information architecture.
 *
 * All routes are locale-prefixed. Every page carries a stable `key` used to:
 *   - resolve translations from the dictionary
 *   - build breadcrumbs
 *   - render sitemap.xml
 */

export type LocaleCode = "en" | "ka";
export const LOCALES: LocaleCode[] = ["en", "ka"];
export const DEFAULT_LOCALE: LocaleCode = "en";

export type RouteKey =
  | "home"
  | "about"
  | "services.index"
  | "services.trade"
  | "services.sourcing"
  | "services.supply"
  | "services.consulting"
  | "commodities.index"
  | "commodities.grains"
  | "commodities.food"
  | "commodities.petrochemical"
  | "compliance.index"
  | "compliance.corporate"
  | "compliance.kyc"
  | "compliance.antifraud"
  | "resources.index"
  | "resources.profile"
  | "resources.downloads"
  | "resources.trade-docs"
  | "contact"
  | "legal.privacy"
  | "legal.cookies"
  | "legal.terms"
  | "legal.accessibility"
  | "legal.notice";

export interface RouteDef {
  key: RouteKey;
  path: string; // path segments after the locale prefix, no leading slash
  parent?: RouteKey;
}

export const ROUTES: Record<RouteKey, RouteDef> = {
  "home":                    { key: "home",                    path: "" },
  "about":                   { key: "about",                   path: "about" },

  "services.index":          { key: "services.index",          path: "services" },
  "services.trade":          { key: "services.trade",          path: "services/international-trade",           parent: "services.index" },
  "services.sourcing":       { key: "services.sourcing",       path: "services/global-sourcing",               parent: "services.index" },
  "services.supply":         { key: "services.supply",         path: "services/commodity-supply",              parent: "services.index" },
  "services.consulting":     { key: "services.consulting",     path: "services/commercial-consulting",         parent: "services.index" },

  "commodities.index":        { key: "commodities.index",        path: "commodities" },
  "commodities.grains":       { key: "commodities.grains",       path: "commodities/grains-cereals",           parent: "commodities.index" },
  "commodities.food":         { key: "commodities.food",         path: "commodities/food-products",            parent: "commodities.index" },
  "commodities.petrochemical":{ key: "commodities.petrochemical",path: "commodities/petrochemical-products",   parent: "commodities.index" },

  "compliance.index":         { key: "compliance.index",         path: "compliance" },
  "compliance.corporate":     { key: "compliance.corporate",     path: "compliance/corporate-information",     parent: "compliance.index" },
  "compliance.kyc":           { key: "compliance.kyc",           path: "compliance/kyc-verification",          parent: "compliance.index" },
  "compliance.antifraud":     { key: "compliance.antifraud",     path: "compliance/anti-fraud-notice",         parent: "compliance.index" },

  "resources.index":          { key: "resources.index",          path: "resources" },
  "resources.profile":        { key: "resources.profile",        path: "resources/company-profile",            parent: "resources.index" },
  "resources.downloads":      { key: "resources.downloads",      path: "resources/downloads",                  parent: "resources.index" },
  "resources.trade-docs":     { key: "resources.trade-docs",     path: "resources/trade-documents",            parent: "resources.index" },

  "contact":                  { key: "contact",                  path: "contact" },

  "legal.privacy":            { key: "legal.privacy",            path: "legal/privacy-policy" },
  "legal.cookies":            { key: "legal.cookies",            path: "legal/cookie-policy" },
  "legal.terms":              { key: "legal.terms",              path: "legal/terms-of-use" },
  "legal.accessibility":      { key: "legal.accessibility",      path: "legal/accessibility-statement" },
  "legal.notice":             { key: "legal.notice",             path: "legal/legal-notice" },
};

export function buildPath(key: RouteKey, locale: LocaleCode): string {
  const def = ROUTES[key];
  return def.path ? `/${locale}/${def.path}` : `/${locale}`;
}

export function breadcrumbFor(key: RouteKey): RouteKey[] {
  const chain: RouteKey[] = [];
  let current: RouteKey | undefined = key;
  while (current) {
    chain.unshift(current);
    current = ROUTES[current].parent;
  }
  if (chain[0] !== "home") chain.unshift("home");
  return chain;
}
